import React, { useMemo, useRef, useState } from "react";
import {
  sdh_importPreview, sdh_importApply, GATE_TONE, ASSESSMENT_BY_VALUE,
  type ImportPreview, type ImportChange,
} from "../api/sdhAPI";
import { useAuth } from "../hooks/useAuth";

/**
 * Bulk SME review: upload the reviewed workbook → validate → PREVIEW the exact change-set
 * (grouped old→new, filterable, selectable) → apply only what the SME confirms.
 * Nothing is written until "Apply"; the preview call is a pure dry run.
 */
const GT = (g?: string) => GATE_TONE[(g || "") as keyof typeof GATE_TONE] || "#8a97a3";
const pretty = (g?: string) => (g || "—").replace("NOT_REVIEWED", "Not reviewed").toLowerCase();

export default function SmeImportPanel(
  { entityId, appNm, onClose, onApplied }:
  { entityId: string; appNm: string; onClose: () => void; onApplied: (msg: string) => void }
) {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [pv, setPv] = useState<ImportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fTrans, setFTrans] = useState<string | null>(null);
  const [fGate, setFGate] = useState<string | null>(null);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [skipped, setSkipped] = useState<Record<string, boolean>>({});
  const inputRef = useRef<HTMLInputElement>(null);

  const runPreview = async (f: File) => {
    setBusy(true); setErr(null); setPv(null); setSkipped({});
    try {
      const res = await sdh_importPreview(entityId, appNm, f);
      setPv(res);
      if (!res.ok) setErr(res.message || "The workbook could not be validated.");
    } catch (e: any) { setErr(e?.response?.data?.detail || e?.message || "Preview failed"); }
    finally { setBusy(false); }
  };

  const changes = pv?.changes || [];
  const shown = useMemo(() => changes.filter((c) =>
    (!fTrans || `${c.from_gate} → ${c.to_gate}` === fTrans) &&
    (!fGate || c.to_gate === fGate) &&
    (!onlyMissing || c.comment_missing)), [changes, fTrans, fGate, onlyMissing]);
  const selected = useMemo(() => shown.filter((c) => !skipped[c.rule_id]), [shown, skipped]);
  const applyIds = useMemo(() => changes.filter((c) => !skipped[c.rule_id]).map((c) => c.rule_id), [changes, skipped]);

  const doApply = async () => {
    if (!file || !applyIds.length) return;
    setBusy(true); setErr(null);
    try {
      const res = await sdh_importApply(entityId, appNm, file,
        user?.user_id || user?.email || "unknown", user?.full_name || user?.email, applyIds);
      onApplied(`Applied ${res?.summary?.applied ?? applyIds.length} decision(s) from the workbook.`);
    } catch (e: any) { setErr(e?.response?.data?.detail || e?.message || "Apply failed"); }
    finally { setBusy(false); }
  };

  const st = pv?.stats;
  return (
    <div className="imp-scrim" onClick={onClose}>
      <div className="imp" onClick={(e) => e.stopPropagation()}>
        <style>{CSS}</style>
        <header className="imp-h">
          <div>
            <div className="eyebrow">BULK SME REVIEW</div>
            <div className="title">Import reviewed workbook</div>
          </div>
          <button className="x" onClick={onClose}>×</button>
        </header>

        <div className="imp-body">
          {/* 1 — file */}
          <section className="imp-step">
            <div className="sh"><b>1</b> Choose the reviewed file</div>
            <div className="drop" onClick={() => inputRef.current?.click()}>
              <input ref={inputRef} type="file" accept=".xlsx,.xlsm" hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); runPreview(f); } }} />
              {file ? <><b>{file.name}</b><span className="mut">click to choose a different file</span></>
                    : <><b>Click to select the .xlsx you reviewed</b>
                        <span className="mut">must contain “Rule ID”, “DQ Decision”, “DQ Comment”</span></>}
            </div>
            {busy && !pv && <div className="mut">Validating…</div>}
            {err && <div className="imp-err">{err}</div>}
            {pv && !pv.ok && (pv.missing_columns?.length ?? 0) > 0 &&
              <div className="imp-err">Missing column(s): {pv.missing_columns!.join(", ")}</div>}
          </section>

          {/* 2 — validation + stats */}
          {pv?.ok && st && (
            <section className="imp-step">
              <div className="sh"><b>2</b> Review what will change <span className="mut">nothing is saved yet</span></div>
              <div className="tiles">
                <Tile n={st.rows_in_file} l="rows in file" />
                <Tile n={st.will_change} l="will change" tone="#2f7a4f" />
                <Tile n={st.unchanged} l="already set" tone="#8a97a3" />
                <Tile n={st.missing_comments} l="no comment" tone="#946011" />
                <Tile n={st.errors} l="errors" tone="#b23a30" />
              </div>
              {st.missing_comments > 0 && (
                <div className="imp-warn">
                  <b>⚠ {st.missing_comments} decision(s) have no comment.</b> They will still be imported, recorded as
                  “{"(no reason given — imported from SME workbook)"}”. Filter them below to fill them in first.
                </div>
              )}
              {(pv.errors?.length ?? 0) > 0 && (
                <details className="imp-errbox">
                  <summary>{pv.errors!.length} row(s) could not be read</summary>
                  {pv.errors!.slice(0, 60).map((e, i) => (
                    <div key={i} className="er"><span className="rw">row {e.excel_row}</span>{e.rule_id} — {e.error}</div>
                  ))}
                </details>
              )}

              <div className="grp-h">How decisions move <span className="mut">click a group to filter</span></div>
              <div className="grps">
                {(pv.by_transition || []).map((g) => (
                  <button key={g.key} className={"grp" + (fTrans === g.key ? " on" : "")}
                    onClick={() => setFTrans(fTrans === g.key ? null : g.key)}>
                    <span className="pill" style={{ background: GT(g.from_gate) }} />
                    <span className="txt">{pretty(g.from_gate)} → <b style={{ color: GT(g.to_gate) }}>{pretty(g.to_gate)}</b></span>
                    <span className="n">{g.count}</span>
                  </button>
                ))}
              </div>

              <div className="grp-h">New status</div>
              <div className="chips">
                {Object.entries(pv.by_new_gate || {}).map(([g, n]) => (
                  <button key={g} className={"chip" + (fGate === g ? " on" : "")} style={{ ["--t" as any]: GT(g) }}
                    onClick={() => setFGate(fGate === g ? null : g)}>{pretty(g)}<em>{n}</em></button>
                ))}
                <button className={"chip" + (onlyMissing ? " on" : "")} style={{ ["--t" as any]: "#946011" }}
                  onClick={() => setOnlyMissing((v) => !v)}>no comment<em>{st.missing_comments}</em></button>
                {(fTrans || fGate || onlyMissing) &&
                  <button className="clr" onClick={() => { setFTrans(null); setFGate(null); setOnlyMissing(false); }}>clear</button>}
              </div>

              {/* 3 — row detail */}
              <div className="grp-h">
                Rules <span className="mut">{shown.length} shown · {selected.length} selected</span>
                <span style={{ flex: 1 }} />
                <button className="lnk" onClick={() => { const n = { ...skipped }; shown.forEach((c) => delete n[c.rule_id]); setSkipped(n); }}>select all shown</button>
                <button className="lnk" onClick={() => { const n = { ...skipped }; shown.forEach((c) => (n[c.rule_id] = true)); setSkipped(n); }}>deselect all shown</button>
              </div>
              <div className="rows">
                {shown.slice(0, 400).map((c: ImportChange) => (
                  <label key={c.rule_id} className={"row" + (skipped[c.rule_id] ? " off" : "")}>
                    <input type="checkbox" checked={!skipped[c.rule_id]}
                      onChange={() => setSkipped((s) => ({ ...s, [c.rule_id]: !s[c.rule_id] }))} />
                    <div className="nm">
                      <div className="t">{c.rule_name || c.rule_id}</div>
                      <div className="f">{c.field || c.rule_id}</div>
                    </div>
                    <div className="mv">
                      <span style={{ color: GT(c.from_gate) }}>{pretty(c.from_gate)}</span>
                      <span className="ar">→</span>
                      <span style={{ color: GT(c.to_gate), fontWeight: 600 }}>
                        {ASSESSMENT_BY_VALUE[c.to_assessment]?.label || c.to_label || pretty(c.to_gate)}
                      </span>
                    </div>
                    <div className={"cm" + (c.comment_missing ? " miss" : "")}>{c.comment_missing ? "⚠ no comment" : c.comment}</div>
                  </label>
                ))}
                {shown.length === 0 && <div className="mut" style={{ padding: 14 }}>No rows match this filter.</div>}
              </div>
            </section>
          )}
        </div>

        <footer className="imp-f">
          <span className="mut">{pv?.ok ? `${applyIds.length} of ${changes.length} change(s) will be applied` : "Select a file to begin"}</span>
          <span style={{ flex: 1 }} />
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn go" disabled={busy || !pv?.ok || !applyIds.length} onClick={doApply}>
            {busy ? "Applying…" : `Apply ${applyIds.length} decision(s)`}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Tile({ n, l, tone }: { n: number; l: string; tone?: string }) {
  return <div className="tile"><span className="n" style={tone ? { color: tone } : undefined}>{n}</span><span className="l">{l}</span></div>;
}

const CSS = `
.imp-scrim{position:fixed;inset:0;z-index:85;background:rgba(14,36,54,.42);display:flex;align-items:center;justify-content:center;padding:24px}
.imp{--line:#e3e8ee;--muted:#6b7c8d;--ink:#0E2436;width:min(1040px,96vw);max-height:92vh;background:#fff;border-radius:16px;
 display:flex;flex-direction:column;overflow:hidden;box-shadow:0 28px 70px rgba(0,0,0,.3);color:var(--ink)}
.imp-h{display:flex;align-items:center;justify-content:space-between;padding:15px 20px;background:#0e2436;color:#fff}
.imp-h .eyebrow{font-size:10px;letter-spacing:.12em;color:#9db4c9}
.imp-h .title{font-size:17px;font-weight:700;font-family:'Space Grotesk',system-ui,sans-serif}
.imp-h .x{all:unset;cursor:pointer;font-size:24px;color:#cdd8ea}.imp-h .x:hover{color:#fff}
.imp-body{overflow:auto;padding:6px 20px 16px}
.imp-step{padding:14px 0;border-bottom:1px solid #eef1f4}
.sh{display:flex;align-items:center;gap:9px;font-size:13.5px;font-weight:700;margin-bottom:10px}
.sh b{display:grid;place-items:center;width:20px;height:20px;border-radius:50%;background:#0e2436;color:#fff;font-size:11px}
.mut{color:var(--muted);font-weight:400;font-size:12px}
.drop{border:1.5px dashed #cfd8e1;border-radius:12px;padding:20px;text-align:center;cursor:pointer;background:#f8fafb}
.drop:hover{border-color:#0e7c7b;background:#f2f8f8}
.drop b{display:block;font-size:13.5px;margin-bottom:3px}
.imp-err{margin-top:10px;background:#fdeceb;color:#b23a30;border-radius:8px;padding:9px 12px;font-size:12.5px}
.imp-warn{margin:10px 0;background:#fbf6ea;border:1px solid #ead9b4;color:#8a6516;border-radius:8px;padding:9px 12px;font-size:12.5px}
.imp-errbox{margin:8px 0;font-size:12px}.imp-errbox summary{cursor:pointer;color:#b23a30}
.imp-errbox .er{padding:3px 0;color:#5a6675}.imp-errbox .rw{font-family:ui-monospace,monospace;color:#8a97a3;margin-right:8px}
.tiles{display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin-bottom:6px}
.tile{border:1px solid var(--line);border-radius:10px;padding:10px 12px;text-align:center}
.tile .n{display:block;font-family:ui-monospace,monospace;font-size:19px;font-weight:600}
.tile .l{font-size:10.5px;color:var(--muted);text-transform:uppercase;letter-spacing:.05em}
.grp-h{display:flex;align-items:center;gap:8px;font-size:12.5px;font-weight:700;margin:16px 0 8px}
.grps{display:flex;flex-direction:column;gap:5px}
.grp{all:unset;cursor:pointer;display:flex;align-items:center;gap:10px;padding:7px 11px;border:1px solid var(--line);border-radius:9px;font-size:13px}
.grp:hover{background:#f6f8fa}.grp.on{border-color:#0e7c7b;background:#f2f8f8}
.grp .pill{width:8px;height:8px;border-radius:50%}
.grp .n{margin-left:auto;font-family:ui-monospace,monospace;font-size:12px;color:var(--muted)}
.chips{display:flex;gap:7px;flex-wrap:wrap}
.chip{all:unset;cursor:pointer;font-size:12px;padding:4px 11px;border-radius:20px;background:#f1f4f7;color:#4a5763;display:inline-flex;gap:6px}
.chip.on{background:color-mix(in srgb,var(--t) 14%,#fff);color:var(--t);box-shadow:inset 0 0 0 1px var(--t)}
.chip em{font-style:normal;font-family:ui-monospace,monospace;font-size:10.5px;opacity:.7}
.clr{all:unset;cursor:pointer;font-size:12px;color:#0e7c7b}.clr:hover{text-decoration:underline}
.lnk{all:unset;cursor:pointer;font-size:11.5px;color:#0e7c7b;margin-left:10px}.lnk:hover{text-decoration:underline}
.rows{border:1px solid var(--line);border-radius:10px;max-height:320px;overflow:auto}
.row{display:grid;grid-template-columns:26px 1fr 260px 240px;gap:10px;align-items:center;padding:8px 11px;border-bottom:1px solid #eef1f4;cursor:pointer}
.row:last-child{border-bottom:0}.row:hover{background:#f8fafb}.row.off{opacity:.45}
.row .nm .t{font-size:13px;font-weight:600}.row .nm .f{font-size:11px;color:var(--muted);font-family:ui-monospace,monospace}
.row .mv{font-size:12px;display:flex;gap:6px;align-items:center}.row .mv .ar{color:#b6c0ca}
.row .cm{font-size:11.5px;color:#5a6675;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.row .cm.miss{color:#946011}
.imp-f{display:flex;align-items:center;gap:10px;padding:13px 20px;border-top:1px solid var(--line);background:#f8fafb}
.btn{all:unset;cursor:pointer;padding:8px 16px;border-radius:9px;font-size:13px;font-weight:600}
.btn.ghost{background:#eef1f5;color:#42586e}
.btn.go{background:#0e7c7b;color:#fff}.btn.go[disabled]{opacity:.45;cursor:not-allowed}
@media(max-width:900px){.tiles{grid-template-columns:repeat(3,1fr)}.row{grid-template-columns:26px 1fr}.row .mv,.row .cm{display:none}}
`;
